import React, { ComponentProps } from "react";
import { DialogTrigger, Dialog, Popover } from "react-aria-components";
import type { DialogRenderProps } from "react-aria-components";

import Button, { ButtonProps } from "./forms/button";
import DropdownPopover from "./dropdown-popover";

import styles from "./popover-menu.module.css";

export type PopoverMenuProps = {
  /**
   * The button that opens it. Every measured call site owns a real button, so
   * this is not a `control` union the way `Modal` is -- see the note below.
   */
  trigger: ButtonProps;
  placement?: ComponentProps<typeof Popover>["placement"];
  children:
    | ((dialogRenderProps: DialogRenderProps) => React.ReactNode)
    | React.ReactNode;
};

/**
 * A button that opens a small panel of arbitrary content, anchored to itself.
 *
 * Not a `Menu`. `Menu` is a list of commands: pick one and it closes. This is
 * the filter-panel shape -- a heading, a column of checkboxes, an action row
 * -- which stays open across selections and has no `menuitem` semantics at
 * all. Folding the two together would make `Menu` mean two things; keeping
 * them apart costs one small component that shares `DropdownPopover` with it.
 *
 * **Deliberately no `control` union**, unlike `Modal`. Every popover call site
 * measured owns a real button trigger, so an `"external"` arm would be
 * speculative API. The bar is "exhaustive w/r/t what the caller needs to do",
 * not "symmetric with the component next door" -- and adding the arm later is
 * the same edit `Modal` just took.
 */
function PopoverMenu({
  trigger,
  placement = "bottom end",
  children,
}: PopoverMenuProps) {
  return (
    <DialogTrigger>
      <Button {...trigger} />
      <DropdownPopover placement={placement} className={styles.popover}>
        <Dialog className={styles.dialog}>
          {(actions) =>
            typeof children === "function" ? children(actions) : children
          }
        </Dialog>
      </DropdownPopover>
    </DialogTrigger>
  );
}

export default PopoverMenu;
